import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, Building2, X } from "lucide-react";
import { useQuery } from "convex/react";
import { api } from "@/convex/_generated/api";
import { Doc, Id } from "@/convex/_generated/dataModel";
import { cn } from "@/lib/utils";
import { ScrollArea } from "@/components/ui/scroll-area";

type Branch = Doc<"branches">;

interface BranchMultiSelectProps {
  selectedBranchIds: Id<"branches">[];
  onSelectionChange: (branchIds: Id<"branches">[]) => void;
  companyId?: Id<"companies">;
  trigger?: React.ReactNode;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
}

export default function BranchMultiSelect({
  selectedBranchIds,
  onSelectionChange,
  companyId,
  trigger,
  required = false,
  disabled = false,
  placeholder = "Seleccionar sucursales...",
}: BranchMultiSelectProps) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [localSelection, setLocalSelection] = useState<Id<"branches">[]>([]);

  // Query branches based on whether we have a companyId or not
  const allBranches = useQuery(
    api.companies.branches.queries.getAllBranches,
    !companyId ? {} : "skip",
  );

  const companyBranches = useQuery(
    api.companies.branches.queries.getBranchesByCompanyId,
    companyId ? { companyId } : "skip",
  );

  const branches = companyId ? companyBranches : allBranches;

  // Sync local selection with prop when dialog opens
  useEffect(() => {
    if (open) {
      setLocalSelection(selectedBranchIds);
    }
  }, [open, selectedBranchIds]);

  // Filter branches based on search
  const filteredBranches =
    branches?.filter((branch) =>
      branch.name.toLowerCase().includes(search.toLowerCase()),
    ) || [];

  // Get selected branch names for display
  const selectedBranches =
    branches?.filter((b) => selectedBranchIds.includes(b._id)) || [];

  const handleToggleBranch = (branchId: Id<"branches">) => {
    setLocalSelection((prev) =>
      prev.includes(branchId)
        ? prev.filter((id) => id !== branchId)
        : [...prev, branchId],
    );
  };

  const handleSelectAll = () => {
    if (filteredBranches.length === localSelection.length) {
      setLocalSelection([]);
    } else {
      setLocalSelection(filteredBranches.map((b) => b._id));
    }
  };

  const handleConfirm = () => {
    onSelectionChange(localSelection);
    setOpen(false);
    setSearch("");
  };

  const handleCancel = () => {
    setLocalSelection(selectedBranchIds);
    setOpen(false);
    setSearch("");
  };

  const handleRemoveBranch = (
    branchId: Id<"branches">,
    e: React.MouseEvent,
  ) => {
    e.stopPropagation();
    onSelectionChange(selectedBranchIds.filter((id) => id !== branchId));
  };

  const defaultTrigger = (
    <Button
      type="button"
      variant="outline"
      disabled={disabled}
      className={cn(
        "w-full min-h-9 h-auto justify-start text-left font-normal",
        required && selectedBranchIds.length === 0 && "border-destructive",
      )}
    >
      {selectedBranches.length > 0 ? (
        <div className="flex flex-wrap gap-1">
          {selectedBranches.slice(0, 3).map((branch) => (
            <Badge
              key={branch._id}
              variant="secondary"
              className="text-xs px-1.5 py-0 h-5 gap-1"
            >
              {branch.name}
              <X
                className="h-3 w-3 cursor-pointer hover:text-destructive"
                onClick={(e) => handleRemoveBranch(branch._id, e)}
              />
            </Badge>
          ))}
          {selectedBranches.length > 3 && (
            <Badge variant="outline" className="text-xs px-1.5 py-0 h-5">
              +{selectedBranches.length - 3} más
            </Badge>
          )}
        </div>
      ) : (
        <div className="flex items-center gap-2 text-muted-foreground">
          <Building2 className="h-4 w-4" />
          <span>{placeholder}</span>
        </div>
      )}
    </Button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger || defaultTrigger}</DialogTrigger>
      <DialogContent className="w-[95vw] max-w-md flex flex-col p-0 overflow-hidden max-h-[85dvh]">
        <DialogHeader className="px-4 py-3 border-b border-border">
          <DialogTitle className="text-base">
            Seleccionar Sucursales
          </DialogTitle>
        </DialogHeader>

        <div className="px-4 py-2 border-b border-border">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar sucursal..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 h-9 text-sm"
            />
          </div>
        </div>

        <div className="px-4 py-2 border-b border-border flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            {localSelection.length} de {filteredBranches.length} seleccionadas
          </span>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 text-xs"
            onClick={handleSelectAll}
          >
            {filteredBranches.length === localSelection.length
              ? "Deseleccionar todas"
              : "Seleccionar todas"}
          </Button>
        </div>

        <ScrollArea className="flex-1 max-h-75">
          <div className="px-4 py-2 space-y-1">
            {branches === undefined ? (
              <div className="text-center py-8 text-muted-foreground">
                <div className="animate-pulse">
                  <Building2 className="h-8 w-8 mx-auto mb-2 text-muted-foreground/30" />
                  <p className="text-sm">Cargando sucursales...</p>
                </div>
              </div>
            ) : filteredBranches.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Building2 className="h-8 w-8 mx-auto mb-2 text-muted-foreground/30" />
                <p className="text-sm">No se encontraron sucursales</p>
                {search && (
                  <p className="text-xs mt-1">Intenta con otros términos</p>
                )}
              </div>
            ) : (
              filteredBranches.map((branch) => {
                const isSelected = localSelection.includes(branch._id);
                const isInactive =
                  branch.internalControl?.state === "inactive";

                return (
                  <div
                    key={branch._id}
                    onClick={() => !isInactive && handleToggleBranch(branch._id)}
                    className={cn(
                      "flex items-center gap-3 p-2.5 rounded-md transition-colors",
                      isInactive
                        ? "opacity-50 cursor-not-allowed bg-muted/30"
                        : "cursor-pointer hover:bg-muted/50",
                      isSelected && !isInactive && "bg-primary/5 ring-1 ring-primary/20",
                    )}
                  >
                    <Checkbox
                      checked={isSelected}
                      disabled={isInactive}
                      onCheckedChange={() =>
                        !isInactive && handleToggleBranch(branch._id)
                      }
                      onClick={(e) => e.stopPropagation()}
                    />

                    <Building2
                      className={cn(
                        "h-4 w-4 shrink-0",
                        isSelected ? "text-primary" : "text-muted-foreground",
                      )}
                    />

                    <div className="flex-1 min-w-0">
                      <p
                        className={cn(
                          "text-sm font-medium truncate",
                          isInactive
                            ? "text-muted-foreground"
                            : "text-foreground",
                        )}
                      >
                        {branch.name}
                      </p>
                      {branch.address && (
                        <p className="text-xs text-muted-foreground truncate">
                          {branch.address}
                        </p>
                      )}
                    </div>

                    {isInactive && (
                      <Badge
                        variant="outline"
                        className="text-[10px] px-1.5 py-0 h-4 shrink-0"
                      >
                        Inactiva
                      </Badge>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </ScrollArea>

        <DialogFooter className="px-4 py-3 border-t border-border gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleCancel}
          >
            Cancelar
          </Button>
          <Button type="button" size="sm" onClick={handleConfirm}>
            Confirmar ({localSelection.length})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
